import { createSignal } from "solid-js";

export type ThemePref = "light" | "dark" | "system";
export type FontSize = "S" | "M" | "L";

export interface ViewStore {
  theme: () => ThemePref;
  setTheme: (t: ThemePref) => void;
  wrap: () => boolean;
  setWrap: (v: boolean) => void;
  fontSize: () => FontSize;
  setFontSize: (s: FontSize) => void;
  showDetailedTimings: () => boolean;
  setShowDetailedTimings: (v: boolean) => void;
  syntaxHighlight: () => boolean;
  setSyntaxHighlight: (v: boolean) => void;
  minimap: () => boolean;
  setMinimap: (v: boolean) => void;
}

const KEY_THEME = "fastdiff:theme";
const KEY_WRAP = "fastdiff:wrap";
const KEY_FONT_SIZE = "fastdiff:fontSize";
const KEY_DETAILED = "fastdiff:detailedTimings";
const KEY_SYNTAX_HIGHLIGHT = "fastdiff:syntaxHighlight";
const KEY_MINIMAP = "fastdiff:minimap";

const VALID_THEMES: ReadonlySet<ThemePref> = new Set(["light", "dark", "system"]);
const VALID_FONT_SIZES: ReadonlySet<FontSize> = new Set(["S", "M", "L"]);

function readEnum<T extends string>(
  key: string,
  allowed: ReadonlySet<T>,
  fallback: T,
): T {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    return v != null && allowed.has(v as T) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function readBool(key: string, fallback: boolean): boolean {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === "1";
  } catch {
    return fallback;
  }
}

function writeSafe(key: string, value: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota or storage disabled — drop silently */
  }
}

export function createViewStore(): ViewStore {
  const [theme, setThemeSig] = createSignal<ThemePref>(
    readEnum(KEY_THEME, VALID_THEMES, "light"),
  );
  const [wrap, setWrapSig] = createSignal<boolean>(
    readBool(KEY_WRAP, false),
  );
  const [fontSize, setFontSizeSig] = createSignal<FontSize>(
    readEnum(KEY_FONT_SIZE, VALID_FONT_SIZES, "M"),
  );
  const [showDetailedTimings, setShowDetailedTimingsSig] = createSignal<boolean>(
    readBool(KEY_DETAILED, false),
  );
  const [syntaxHighlight, setSyntaxHighlightSig] = createSignal<boolean>(
    readBool(KEY_SYNTAX_HIGHLIGHT, true),
  );
  const [minimap, setMinimapSig] = createSignal<boolean>(
    readBool(KEY_MINIMAP, true),
  );

  const setTheme = (t: ThemePref) => {
    setThemeSig(t);
    writeSafe(KEY_THEME, t);
  };
  const setWrap = (v: boolean) => {
    setWrapSig(v);
    writeSafe(KEY_WRAP, v ? "1" : "0");
  };
  const setFontSize = (s: FontSize) => {
    setFontSizeSig(s);
    writeSafe(KEY_FONT_SIZE, s);
  };
  const setShowDetailedTimings = (v: boolean) => {
    setShowDetailedTimingsSig(v);
    writeSafe(KEY_DETAILED, v ? "1" : "0");
  };
  const setSyntaxHighlight = (v: boolean) => {
    setSyntaxHighlightSig(v);
    writeSafe(KEY_SYNTAX_HIGHLIGHT, v ? "1" : "0");
  };
  const setMinimap = (v: boolean) => {
    setMinimapSig(v);
    writeSafe(KEY_MINIMAP, v ? "1" : "0");
  };

  return {
    theme, setTheme,
    wrap, setWrap,
    fontSize, setFontSize,
    showDetailedTimings, setShowDetailedTimings,
    syntaxHighlight, setSyntaxHighlight,
    minimap, setMinimap,
  };
}
